import React from "react";
import {SvgIcon} from "@mui/material";
import FormatAlignCenterIcon from "@mui/icons-material/FormatAlignCenter";
import FormatAlignJustifyIcon from "@mui/icons-material/FormatAlignJustify";
import FormatAlignLeftIcon from "@mui/icons-material/FormatAlignLeft";
import FormatAlignRightIcon from "@mui/icons-material/FormatAlignRight";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import {DEFAULT_PRINTOUT_PARAGRAPH_VARIANT, type PrintoutParagraphVariant} from "../../blocks";
import {
    HEADING_LEVEL_BY_BLOCK_FORMAT,
    isParagraphBlockFormat,
    type BlockFormat,
    type HeadingLevel,
    type ListFormat,
    type TextAlignment,
} from "./printoutToolbarFormats";

const PARAGRAPH_ICON_PATH = 'M280-160v-520H80v-120h520v120H400v520H280Zm360 0v-320H520v-120h360v120H760v320H640Z';
const HEADING_ICON_PATHS: Record<HeadingLevel, string> = {
    1: 'M200-280v-400h80v160h160v-160h80v400h-80v-160H280v160h-80Zm480 0v-320h-80v-80h160v400h-80Z',
    2: 'M120-280v-400h80v160h160v-160h80v400h-80v-160H200v160h-80Zm400 0v-160q0-33 23.5-56.5T600-520h160v-80H520v-80h240q33 0 56.5 23.5T840-600v80q0 33-23.5 56.5T760-440H600v80h240v80H520Z',
    3: 'M120-280v-400h80v160h160v-160h80v400h-80v-160H200v160h-80Zm400 0v-80h240v-80H600v-80h160v-80H520v-80h240q33 0 56.5 23.5T840-600v240q0 33-23.5 56.5T760-280H520Z',
    4: 'M120-280v-400h80v160h160v-160h80v400h-80v-160H200v160h-80Zm600 0v-120H520v-280h80v200h120v-200h80v200h80v80h-80v120h-80Z',
};

const PARAGRAPH_ICON_SCALES: Record<PrintoutParagraphVariant, number> = {
    body1: 1,
    body2: 0.84,
    caption: 0.7,
    displayTitle: 1.1,
    largeValue: 1.05,
    detailValue: 1.02,
};

// Scale inside the viewBox so every icon keeps the same outer dimensions.
const getParagraphIconViewBox = (scale: number) => {
    const size = 960 / scale;
    const inset = (size - 960) / 2;

    return `${-inset} ${-960 - inset} ${size} ${size}`;
};

export const BlockFormatIcon = ({format}: {format: BlockFormat}) => {
    const headingLevel = HEADING_LEVEL_BY_BLOCK_FORMAT[format];

    if (!headingLevel) {
        const variant = isParagraphBlockFormat(format) ? format : DEFAULT_PRINTOUT_PARAGRAPH_VARIANT;

        return (
            <SvgIcon fontSize="small" viewBox={getParagraphIconViewBox(PARAGRAPH_ICON_SCALES[variant])}>
                <path d={PARAGRAPH_ICON_PATH}/>
            </SvgIcon>
        );
    }

    return (
        <SvgIcon fontSize="small" viewBox="0 -960 960 960">
            <path d={HEADING_ICON_PATHS[headingLevel]}/>
        </SvgIcon>
    );
};

export const ListFormatIcon = ({format}: {format: ListFormat}) => {
    if (format === 'ordered') {
        return <FormatListNumberedIcon fontSize="small"/>;
    }

    return <FormatListBulletedIcon fontSize="small"/>;
};

export const AlignmentIcon = ({alignment}: {alignment: TextAlignment}) => {
    switch (alignment) {
        case 'center':
            return <FormatAlignCenterIcon fontSize="small"/>;
        case 'right':
            return <FormatAlignRightIcon fontSize="small"/>;
        case 'justify':
            return <FormatAlignJustifyIcon fontSize="small"/>;
        case 'left':
        default:
            return <FormatAlignLeftIcon fontSize="small"/>;
    }
};
